// Google Play Billing via the Digital Goods API + Payment Request API, used
// only inside the Debii TWA (Trusted Web Activity) on Android. The browser only
// ever obtains a purchaseToken; the backend verifies it and is the ONLY thing
// that grants Debii Plus. Web (debiiapp.com) continues to use Paystack and never
// touches this module.
import api from "@/lib/api";

const PLAY_STORE = "https://play.google.com/billing";

// Whether the Google Play billing bridge is present (TWA on Android installed
// from Play). In a normal browser / Preview this is false and we fall back.
export function isPlayBillingAvailable() {
  return typeof window !== "undefined" && "getDigitalGoodsService" in window;
}

async function playService() {
  if (!isPlayBillingAvailable()) {
    throw new Error("play_billing_unavailable");
  }
  return window.getDigitalGoodsService(PLAY_STORE);
}

// Fetch the localized price/title straight from Play (never hard-coded).
export async function getPlanDetails(sku) {
  const service = await playService();
  const details = await service.getDetails([sku]);
  const item = (details || []).find((d) => d.itemId === sku) || details?.[0];
  if (!item) throw new Error("product_unavailable");
  return item;
}

// Launch the Play purchase sheet and verify the resulting token on the backend.
export async function purchasePlan(sku) {
  const service = await playService();
  const methodData = [{ supportedMethods: PLAY_STORE, data: { sku } }];
  // Required by the Payment Request constructor; Play uses the Console price.
  const details = { total: { label: "Debii Plus", amount: { currency: "USD", value: "0" } } };
  const request = new PaymentRequest(methodData, details);
  const response = await request.show();
  const purchaseToken = response?.details?.purchaseToken;
  if (!purchaseToken) {
    await response.complete("fail");
    throw new Error("no_purchase_token");
  }
  try {
    const { data } = await api.post("/billing/google-play/verify", {
      productId: sku,
      purchaseToken,
    });
    await response.complete(data?.entitled ? "success" : "fail");
    if (!data?.entitled) throw new Error("not_entitled");
    return data;
  } catch (e) {
    await response.complete("fail");
    throw e;
  }
}

// Re-sync existing Play purchases to this signed-in account (reinstall, new
// device, or purchase made before logging in). The backend re-verifies each.
export async function restorePurchases(sku) {
  const service = await playService();
  const purchases = (await service.listPurchases()) || [];
  const tokens = purchases
    .filter((p) => p.itemId === sku && p.purchaseToken)
    .map((p) => p.purchaseToken);
  const { data } = await api.post("/billing/google-play/restore", { tokens });
  return data;
}
